import { Button, Grid } from '@design-systems/canvas-ui'
import React, { useEffect, useRef } from 'react'
import {
    Html5Qrcode,
    Html5QrcodeSupportedFormats,
    Html5QrcodeScannerState,
} from 'html5-qrcode'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'

interface BarcodeScannerProps {
    isScannerActive: boolean
    onScan: (value: string) => void
    onClose: () => void
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
    isScannerActive,
    onScan,
    onClose,
}) => {
    const scannerRef = useRef<Html5Qrcode | null>(null)
    const isStartingRef = useRef(false)
    const { t } = useEssentials()
    const { itemSearch } = TRANSLATION_KEYS
    useEffect(() => {
        const startScanner = async () => {
            if (isStartingRef.current) return
            isStartingRef.current = true

            try {
                if (!scannerRef.current) {
                    scannerRef.current = new Html5Qrcode('scanner')
                }

                const state = scannerRef.current.getState()
                if (
                    state === Html5QrcodeScannerState.SCANNING ||
                    state === Html5QrcodeScannerState.PAUSED
                ) {
                    return // Already scanning
                }

                const config = {
                    fps: 15,
                    qrbox: { width: 200, height: 100 }, // Larger qrbox for better focus
                    formatsToSupport: [
                        Html5QrcodeSupportedFormats.UPC_A,
                        Html5QrcodeSupportedFormats.CODE_39,
                        Html5QrcodeSupportedFormats.CODE_128,
                    ],
                }

                await scannerRef.current.start(
                    { facingMode: 'environment' },
                    config,
                    (decodedText) => {
                        onScan(decodedText)
                        onClose()
                    },
                    () => {},
                )
            } catch {
                // silently handle error to prevent app from crashing
            } finally {
                isStartingRef.current = false
            }
        }

        if (isScannerActive) {
            void startScanner()
        }

        return () => {
            const cleanup = async () => {
                if (scannerRef.current) {
                    const state = scannerRef.current.getState()

                    if (
                        state === Html5QrcodeScannerState.SCANNING ||
                        state === Html5QrcodeScannerState.PAUSED
                    ) {
                        try {
                            await scannerRef.current.stop()
                            await scannerRef.current.clear()
                        } catch {
                            // Error during cleanup
                        } finally {
                            scannerRef.current = null
                        }
                    }

                    const el = document.getElementById('scanner')
                    if (el) el.innerHTML = ''
                }
            }

            void cleanup()
        }
    }, [isScannerActive, onScan, onClose])

    const handleStopScanning = async () => {
        if (scannerRef.current) {
            const state = scannerRef.current.getState()

            if (
                state === Html5QrcodeScannerState.SCANNING ||
                state === Html5QrcodeScannerState.PAUSED
            ) {
                try {
                    await scannerRef.current.stop()
                    await scannerRef.current.clear()
                } catch {
                    // silently handle error to prevent app from crashing
                } finally {
                    scannerRef.current = null
                    const el = document.getElementById('scanner')
                    if (el) el.innerHTML = ''
                    onClose()
                }
            }
        }
    }

    return (
        <div className="barcode-scanner">
            {isScannerActive && (
                <Grid.Container justify="center" className="hc8-mt-6x">
                    <div
                        id="scanner"
                        style={{
                            width: '100%',
                            maxWidth: '300px',
                            marginTop: '1rem',
                        }}
                    ></div>
                    <Grid.Item
                        className="hc8-mt-6x"
                        size={{
                            xs: 6,
                        }}
                    >
                        <Button onClick={handleStopScanning}>
                            {t(itemSearch.stopScanning)}
                        </Button>
                    </Grid.Item>
                </Grid.Container>
            )}
        </div>
    )
}

export default BarcodeScanner
